import { formElementHelper, formHelper, FormSchema } from '@jht/react-library';
import { Typography } from '@material-ui/core';
import { IFITGrid } from './product-form-schemas';

const contactPersonSchema = {
  inputNames: ['role', 'name', 'email', 'phone'],
  inputLabels: ['Role', 'Name', 'Email Address', 'Phone Number'],
};

const makeAdditionalContactElements = productDetails => {
  const additionalContactCount =
    Math.max(
      ...Object.keys(productDetails)
        .filter(key => /contact_/.test(key))
        .map(key => Number(key.charAt(8))),
    ) + 1;
  const elements = [];

  if (additionalContactCount > 0) {
    elements.push(
      formElementHelper({
        fullWidth: true,
        name: 'header',
        component: () => {
          return (
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
              Additional Contacts
            </Typography>
          );
        },
      }),
    );

    Array(additionalContactCount)
      .fill(contactPersonSchema)
      .forEach((schema, i) => {
        schema.inputNames.forEach((field, fieldIdx) => {
          elements.push(
            formElementHelper({
              fullWidth: true,
              col: 3,
              name: `contact_${i}_${field}`,
              label: schema.inputLabels[fieldIdx],
              value: productDetails[`contact_${i}_${field}`],
              inputType: 'input',
              type: 'text',
              disabled: true,
            }),
          );
        });
      });
  }
  return elements;
};

export const orgNameFormSchema = (orgName): FormSchema => {
  return formHelper([
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: 'organizationName',
      label: 'Organization Name',
      value: orgName,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  ]);
};

export const facilityFormSchema = (facilities, facilityMap): FormSchema => {
  const elements = [];
  facilities.forEach((facility, index) => {
    elements.push(
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: `facilityName_${index}`,
        label: 'Facility Name',
        value: facility.name || facilityMap[facility.facilityId]?.name,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: `facilityNickName_${index}`,
        label: 'Facility NickName',
        value: facility.nickname || facilityMap[facility.facilityId]?.nickname,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: `street1_${index}`,
        label: 'Street',
        value: facility.street1 || facilityMap[facility.facilityId]?.address?.street1,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    );
    if (facility.street2 || facilityMap[facility.facilityId]?.address?.street2) {
      elements.push(
        formElementHelper({
          fullWidth: true,
          col: 12,
          name: `street2_${index}`,
          label: 'Street',
          value: facility.street2 || facilityMap[facility.facilityId].address.street2,
          inputType: 'input',
          type: 'text',
          disabled: true,
        }),
      );
    }
    elements.push(
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: `city_${index}`,
        label: 'City',
        value: facility.city || facilityMap[facility.facilityId]?.address?.city,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    );
    if (index < facilities.length - 1) {
      elements.push(
        formElementHelper({
          fullWidth: true,
          name: `divider_${index}`,
          component: DividerElement,
        }),
      );
    }
  });
  return formHelper(elements);
};

export const productOrderDetailsFormSchema = (productDetails): FormSchema => {
  return formHelper(
    [
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'smallBusinessUnit',
        label: 'Strategic Business Unit',
        value: productDetails.smallBusinessUnit,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'desiredGoLiveDate',
        label: 'Desired Go Live Date',
        value: productDetails.desiredGoLiveDate,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'desiredGoLiveDateNotes',
        label: 'Notes',
        value: productDetails.desiredGoLiveDateNotes,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'salesContactName',
        label: 'Sales Contact Name',
        value: productDetails.salesContactName,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'salesContactEmail',
        label: 'Sales Contact Email',
        value: productDetails.salesContactEmail,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'salesContactPhone',
        label: 'Sales Contact Phone',
        value: productDetails.salesContactPhone,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'customerContactName',
        label: 'Customer Contact Name',
        value: productDetails.customerContactName,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'customerContactEmail',
        label: 'Customer Contact Email',
        value: productDetails.customerContactEmail,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        fullWidth: true,
        col: 4,
        name: 'customerContactPhone',
        label: 'Customer Contact Phone',
        value: productDetails.customerContactPhone,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    ].concat(makeAdditionalContactElements(productDetails)),
  );
};

export const netpulseOrderDetailsFormSchema = (productDetails): FormSchema => {
  const elements = [];
  elements.push(
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: 'chainName',
      label: 'Organization Name',
      value: productDetails.chainName,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: 'companyWebsite',
      label: 'Company Website',
      value: productDetails.companyWebsite,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: 'existingMobileApp',
      label: 'Existing Mobile App?',
      value: productDetails.existingMobileApp === '1' ? 'Yes' : 'No',
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: productDetails.mobileAppName ? 'mobileAppName' : 'desiredMobileAppName',
      label: productDetails.mobileAppName ? 'Mobile App Name' : 'Desired Mobile App Name',
      value: productDetails.mobileAppName || productDetails.desiredMobileAppName,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  );
  if (productDetails.productSelection.COM360) {
    elements.push(
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'existingWorkoutTrackingNetworkWebsite',
        label: 'Existing Workout Tracking Network Web?',
        value: productDetails.existingWorkoutTrackingNetworkWebsite === '1' ? 'Yes' : 'No',
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    );
    elements.push(
      formElementHelper({
        fullWidth: true,
        col: 12,
        name: 'workoutTrackingNetworkSubscriptionTerm',
        label: 'Subscription Term',
        value: productDetails.workoutTrackingNetworkSubscriptionTerm,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    );
  }
  elements.push(
    formElementHelper({
      fullWidth: true,
      col: 12,
      name: 'orderingNotes',
      label: 'Ordering Notes',
      value: productDetails.orderingNotes,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  );
  return formHelper(elements);
};

export const COM360SubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      col: 6,
      name: 'c360Type',
      label: 'Community 360 App Type',
      value:
        (values.c360Type === 'c360_basic_app' && 'Community 360 - Basic') ||
        (values.c360Type === 'c360_advanced_app' && 'Community 360 - Advanced') ||
        (values.c360Type === 'c360_pro_app' && 'Community 360 - Pro'),
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      col: 6,
      name: 'orderDuration',
      label: 'Order Duration',
      value: values.orderDuration,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  ]);
};

export const E360SubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      col: 6,
      name: 'e360Type',
      label: 'Engage 360 Type',
      value: values.e360Type === 'engage_360_advanced' ? 'Engage 360 Advanced' : 'Engage 360 Pro',
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      col: 6,
      name: 'orderDuration',
      label: 'Order Duration',
      value: values.orderDuration,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  ]);
};

export const AMSubFormSchema = (values?: any): FormSchema => {
  // empty form needed to conform with other parts of digital order
  return formHelper([]);
};

export const Facility360SubFormSchema = (values?: any): FormSchema => {
  // empty form needed to conform with other parts of digital order
  return formHelper([]);
};

export const FacilityConciergeSubFormSchema = (values?: any): FormSchema => {
  // empty form needed to conform with other parts of digital order
  return formHelper([]);
};

export const EGYMSubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      col: 6,
      name: 'egymClientId',
      label: 'EGYM Client ID',
      value: values.egymClientId,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      col: 6,
      name: 'egymClientSecret',
      label: 'EGYM Client Secret',
      value: values.egymClientSecret,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
  ]);
};

const tenantRegionLabel = region =>
  region === 'united_states' ? 'United States' : region === 'european' ? 'European' : region;

export const PACESubFormSchema = (values): FormSchema => {
  // Only render the selected partner's fields (the form library reserves a grid
  // column for every element, so hidden fields would leave empty gaps).
  const partnerFields = {
    egym: [
      formElementHelper({
        col: 3,
        name: 'egymClientId',
        label: 'EGYM Client ID',
        value: values.egymClientId,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 3,
        name: 'egymClientSecret',
        label: 'EGYM Client Secret',
        value: values.egymClientSecret,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 6,
        name: 'egymTenantId',
        label: 'Tenant ID',
        value: values.egymTenantId,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 6,
        name: 'egymTenantRegion',
        label: 'Tenant Region',
        value: tenantRegionLabel(values.egymTenantRegion),
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    ],
    magicline: [
      formElementHelper({
        col: 3,
        name: 'magiclineApiKey',
        label: 'API Key',
        value: values.magiclineApiKey,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 3,
        name: 'magiclineBaseUrl',
        label: 'Base URL',
        value: values.magiclineBaseUrl,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 6,
        name: 'magiclineTenantId',
        label: 'Tenant ID',
        value: values.magiclineTenantId,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 6,
        name: 'magiclineTenantRegion',
        label: 'Tenant Region',
        value: tenantRegionLabel(values.magiclineTenantRegion),
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    ],
    matrix: [
      formElementHelper({
        col: 6,
        name: 'matrixTenantId',
        label: 'Tenant ID',
        value: values.matrixTenantId,
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
      formElementHelper({
        col: 6,
        name: 'matrixTenantRegion',
        label: 'Tenant Region',
        value: tenantRegionLabel(values.matrixTenantRegion),
        inputType: 'input',
        type: 'text',
        disabled: true,
      }),
    ],
  };

  return formHelper([
    formElementHelper({
      col: 6,
      name: 'integrationPartner',
      label: 'Integration Partner',
      value: values.integrationPartner,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    ...(partnerFields[values.integrationPartner] || []),
  ]);
};

export const iFitIntegrationSubFormSchema = (
  values,
  facilityNames,
  facilityExistence,
): FormSchema => {
  return formHelper([
    formElementHelper({
      fullWidth: true,
      component: () => {
        return (
          <>
            <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)', marginTop: '1rem' }}>
              Note:
            </Typography>
            {facilityExistence.includes(false) && (
              <Typography
                variant="body2"
                style={{ color: 'rgba(0, 0, 0, 0.75)', marginBottom: '0.5rem' }}
              >
                Select the scenario that applies for this existing facilities order below: Note -
                You can select more than one option if required, but this will be rare.
              </Typography>
            )}
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              iFIT is automatically included for this new facility order unless this is for Touch
              Base/ Touch XL Base. If this is a Touch Base/Touch XL Base order that needs iFIT,
              select modalities in the Touch Base/Touch XL Base column to enable iFIT ordering.
            </Typography>
          </>
        );
      },
      show: facilityExistence.includes(false),
    }),
    formElementHelper({
      fullWidth: true,
      value: values,
      name: 'iFitGrid',
      facilityNames: facilityNames,
      facilityExistence: facilityExistence,
      disabled: true,
      readOnly: true,
      component: props => <IFITGrid {...props} />,
    }),
  ]);
};

export const ecoFitIntegrationSubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      fullWidth: true,
      component: () => {
        return (
          <>
            <Typography variant="body1" style={{ color: 'rgba(0, 0, 0, 1)', marginTop: '1rem' }}>
              Note:
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
              Please only check this box and order Ecofit for international markets needing an
              integration with Ecofit <b>(This is not the same as Facility 360)</b>.
            </Typography>
          </>
        );
      },
    }),
  ]);
};

export const YMCA360IntegrationSubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      fullWidth: true,
      component: () => (
        <>
          <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
            Integration is facility-wide (all equipment)
          </Typography>
          <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
            No cost associated with activation
          </Typography>
        </>
      ),
    }),
  ]);
};

export const intelligentCycleSubformSchema = (): FormSchema => {
  return formHelper([
    formElementHelper({
      fullWidth: true,
      component: () => (
        <>
          <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
            This is a 6 month free trial of the Intelligent Cycling program on Matrix CX Series
            Cycles. (US and Netherlands only)
          </Typography>
        </>
      ),
    }),
  ]);
};

export const otherIntegrationSubFormSchema = (values): FormSchema => {
  return formHelper([
    formElementHelper({
      col: 6,
      name: 'integrationName',
      label: 'Integration Name',
      value: values.integrationName,
      inputType: 'input',
      type: 'text',
      disabled: true,
    }),
    formElementHelper({
      col: 6,
      name: 'integrationDetails',
      label: 'Integration Details',
      value: values?.integrationDetails,
      inputType: 'multiline',
      variant: 'outlined',
      type: 'text',
      disabled: true,
    }),
  ]);
};

const DividerElement = () => {
  return <div></div>;
};
